import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import { payrollOpsService } from "../../../services/api";
import PayrollOneTimeInputsPanel from "./PayrollOneTimeInputsPanel";
import RunPayrollStepViews from "./RunPayrollStepViews";
import PayrollChecklistPanel from "./PayrollChecklistPanel";
import PayrollBreakdownModal from "./PayrollBreakdownModal";
import ProcessPayrollPanel, {
  ProcessPayrollActions,
  countNegativeNetFromRows,
} from "./ProcessPayrollPanel";
import PayrollActionChooserModal from "./PayrollActionChooserModal";
import PayrollFinalisePanel, { FinalisePayrollActions } from "./PayrollFinalisePanel";
import PayrollDisbursementPanel, { DisbursementActions } from "./PayrollDisbursementPanel";
import PayrollPayslipsPanel from "./PayrollPayslipsPanel";
import {
  chunkIds,
  getPreviousPayMonth,
  formatPayMonthLabel,
  resolvePayMonthSelectableMax,
  resolveDefaultPayMonth,
  isEmployeeOutOfPayMonth,
} from "./runPayrollUtils";
import PayrollStepActions from "./PayrollStepActions";
import ApplyAutoStructuresModal from "./ApplyAutoStructuresModal";
import ProcessWorkflowShell from "../shared/ProcessWorkflowShell";
import ProcessWizardStrip from "../shared/ProcessWizardStrip";
import {
  persistProcessWizardLanding,
  resolveProcessWizardLanding,
} from "../shared/processWizardLanding";
import PayMonthNav from "../shared/PayMonthNav";
import { resolveCompanyMinMonthValue, clampYm } from "../../../utils/selectableDateBounds";
import { messageAttendanceNotFinalised } from "../../../lib/payrollAttendanceGateMessages";
import { DsButton } from "../../../design-system";
import { usePermissions } from "../../../rbac/PermissionsContext";
import {
  PAYROLL_STEP_RBAC,
  canApproveProcessStep,
  canEditProcessStep,
  canViewProcessStep,
} from "../../../rbac/processStepAccess";

// Process/Reprocess take ~5–7 s per employee on a remote DB; small batches keep each request short.
const PROCESS_CHUNK_SIZE = 5;
const UNPROCESS_CHUNK_SIZE = 15;
const POST_PROCESS_KEYS = new Set([
  "checklist",
  "company_checklist",
  "disbursement",
  "preview_validate",
  "finalise",
  "payslips",
]);
const REVIEW_WHEN_LOCKED_KEYS = new Set(["preview_validate"]);

const STEP_DISPLAY_LABELS = {
  one_time_inputs: "Monthly Variables",
  attendance_validation: "Attendance",
};

function stepDisplayLabel(step) {
  if (!step) return "";
  return STEP_DISPLAY_LABELS[step.key] || step.label || step.key;
}

function isPostProcessStep(key) {
  return POST_PROCESS_KEYS.has(key);
}

function payMonthNow() {
  return new Date().toISOString().slice(0, 7);
}

function resolveInitialPayMonth(scopePayMonth) {
  const min = resolveCompanyMinMonthValue();
  const scoped = scopePayMonth ? String(scopePayMonth).slice(0, 7) : "";
  if (/^\d{4}-\d{2}$/.test(scoped)) {
    return clampYm(scoped, min, null);
  }
  // Default = service commencement month (not calendar "today")
  if (/^\d{4}-\d{2}$/.test(String(min || ""))) return min;
  return payMonthNow();
}

export default function RunPayrollWizard({
  darkMode = false,
  scopeContext = null,
  onPayMonthChange = null,
  canApprove = true,
  salaryStructures = null,
  statutoryConfigJson = null,
  branchesList = null,
}) {
  // Default pay month = service commencement (not calendar today / parent month).
  const initialMonth = resolveInitialPayMonth(null);
  const { hasScreenAccess, isAdmin } = usePermissions();

  const [payMonth, setPayMonth] = useState(initialMonth);
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeStepKey, setActiveStepKeyState] = useState("new_joinees");
  const [visitedKeys, setVisitedKeys] = useState(() => new Set(["new_joinees"]));
  const [stepBusy, setStepBusy] = useState(false);

  const setActiveStepKey = useCallback((key) => {
    if (!key) return;
    setVisitedKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    setActiveStepKeyState(key);
    const runId = Number(run?.id) || 0;
    const month = String(payMonthRef.current || "").slice(0, 7);
    if (runId && month) {
      persistProcessWizardLanding(`payroll-wizard-step:${runId}:${month}`, key);
    }
  }, [run?.id]);

  const clearStepValidation = useCallback((key) => {
    if (!key || key === "process" || key === "finalise" || key === "disbursement" || key === "payslips") {
      return;
    }
    setRun((prev) => {
      if (!prev?.stepsJson) return prev;
      const stepsJson = (prev.stepsJson || []).map((s) =>
        s.key === key ? { ...s, status: "pending", resultJson: null } : s
      );
      return { ...prev, stepsJson };
    });
  }, []);

  const [actionChooser, setActionChooser] = useState(null);
  const [breakdownRow, setBreakdownRow] = useState(null);
  const [processedRows, setProcessedRows] = useState([]);
  const [revealNegativeKey, setRevealNegativeKey] = useState(0);
  const [listTabSignal, setListTabSignal] = useState(null);
  /** Server still busy on this run (timed-out batch or another user's request). */
  const [serverBusy, setServerBusy] = useState(null);
  const [pendingResume, setPendingResume] = useState(null);
  const [ignoredLeaveRequestIds, setIgnoredLeaveRequestIds] = useState([]);
  /** New Joinees: bulk first-map structure apply */
  const [applyStructuresOpen, setApplyStructuresOpen] = useState(false);
  /** false = discrepancies only (default); true = all employees in TDS Comparison */
  const [showAllTds, setShowAllTds] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null);
  const [prevMonthBlocked, setPrevMonthBlocked] = useState(false);
  /** YYYY-MM list of company months with finalizedAt (for pay-month max). */
  const [finalizedMonths, setFinalizedMonths] = useState([]);
  /** Sticky max from API — does not shrink when earlier months are unlocked. */
  const [openedThroughMonth, setOpenedThroughMonth] = useState(null);
  const [finalizedMonthsReady, setFinalizedMonthsReady] = useState(false);
  const [disbursementRows, setDisbursementRows] = useState([]);
  const [disbursementTick, setDisbursementTick] = useState(0);
  const [payslipReloadTick, setPayslipReloadTick] = useState(0);
  const [processHeaderExtras, setProcessHeaderExtras] = useState(null);
  /** Which Pay/Hold/Partial/Unpay button is fetching the chooser list (null = none). */
  const [disbursementLoadingAction, setDisbursementLoadingAction] = useState(null);
  /** both | salary | fnf */
  const [disburseStreamMode, setDisburseStreamMode] = useState("both");
  const [disbursePartialForm, setDisbursePartialForm] = useState({
    holdMode: "amount",
    holdAmount: "",
    notes: "",
  });
  const [disbursementPaymentDate, setDisbursementPaymentDate] = useState(
    () => new Date().toISOString().slice(0, 10)
  );
  const applyingWizardMonthRef = useRef(false);
  const clampedToMaxToastRef = useRef(null);
  const bootstrappedPayMonthRef = useRef(false);
  const autoLandingRunKeyRef = useRef(null);
  const payMonthRef = useRef(payMonth);
  payMonthRef.current = payMonth;
  const loadSeqRef = useRef(0);
  const relievedCleanupToastKeyRef = useRef("");

  useEffect(() => {
    setVisitedKeys(new Set(["new_joinees"]));
  }, [payMonth]);

  const listedEmployeesRaw = scopeContext?.listedEmployees || [];
  /** Two-way payroll check: exclude master leavers whose LWD is before this pay month */
  const listedEmployees = useMemo(
    () =>
      (listedEmployeesRaw || []).filter((e) => !isEmployeeOutOfPayMonth(e, payMonth)),
    [listedEmployeesRaw, payMonth]
  );

  const [statutoryOverridesByEmployeeId, setStatutoryOverridesByEmployeeId] = useState(
    () => new Map()
  );

  const listedEmployeeIdsKey = useMemo(
    () =>
      (listedEmployees || [])
        .map((e) => e?.id)
        .filter(Boolean)
        .join(","),
    [listedEmployees]
  );

  useEffect(() => {
    const ids = listedEmployeeIdsKey
      ? listedEmployeeIdsKey.split(",").map((id) => Number(id))
      : [];
    if (!ids.length || !payMonth) {
      setStatutoryOverridesByEmployeeId(new Map());
      return undefined;
    }
    const asOf = `${String(payMonth).slice(0, 7)}-01`;
    let cancelled = false;
    payrollOpsService
      .resolveStatutoryBatch(ids, asOf)
      .then((res) => {
        if (cancelled) return;
        const raw = res?.data || {};
        const map = new Map();
        Object.entries(raw).forEach(([k, v]) => {
          map.set(Number(k), v);
          map.set(String(k), v);
        });
        setStatutoryOverridesByEmployeeId(map);
      })
      .catch(() => {
        if (!cancelled) setStatutoryOverridesByEmployeeId(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [listedEmployeeIdsKey, payMonth]);
  const companyMinMonth = useMemo(() => resolveCompanyMinMonthValue(), []);
  const companyMaxMonth = useMemo(() => {
    if (!finalizedMonthsReady) return null;
    return resolvePayMonthSelectableMax(
      finalizedMonths,
      companyMinMonth,
      openedThroughMonth
    );
  }, [finalizedMonths, companyMinMonth, finalizedMonthsReady, openedThroughMonth]);

  const refreshFinalizedMonths = useCallback(async () => {
    try {
      const res = await payrollOpsService.listFinalizedPayrollMonths();
      const months = (Array.isArray(res?.data) ? res.data : [])
        .map((row) => String(row?.payMonth || "").slice(0, 7))
        .filter((m) => /^\d{4}-\d{2}$/.test(m));
      setFinalizedMonths(months);
      const sticky = String(res?.selectableMaxMonth || "").slice(0, 7);
      setOpenedThroughMonth(/^\d{4}-\d{2}$/.test(sticky) ? sticky : null);
    } catch {
      /* keep last known list */
    } finally {
      setFinalizedMonthsReady(true);
    }
  }, []);

  useEffect(() => {
    refreshFinalizedMonths();
  }, [refreshFinalizedMonths]);

  const applyPayMonth = useCallback(
    (month, { notifyParent = true, toastIfClamped = false } = {}) => {
      const clamped = clampYm(month, companyMinMonth, companyMaxMonth);
      if (!clamped) return;
      if (clamped === payMonth) return;
      if (toastIfClamped && clamped !== String(month || "").slice(0, 7) && companyMaxMonth) {
        const toastKey = `${clamped}|${companyMaxMonth}`;
        if (clampedToMaxToastRef.current !== toastKey) {
          clampedToMaxToastRef.current = toastKey;
        }
      }
      applyingWizardMonthRef.current = true;
      setPayMonth(clamped);
      setShowAllTds(false);
      if (notifyParent) onPayMonthChange?.(clamped);
      setTimeout(() => {
        applyingWizardMonthRef.current = false;
      }, 0);
    },
    [payMonth, onPayMonthChange, companyMinMonth, companyMaxMonth]
  );

  useEffect(() => {
    if (!finalizedMonthsReady || !companyMaxMonth || !companyMinMonth) return;
    if (bootstrappedPayMonthRef.current) return;
    bootstrappedPayMonthRef.current = true;
    const def = resolveDefaultPayMonth({
      companyMinMonth,
      companyMaxMonth,
      finalizedMonths,
      currentMonth: payMonthNow(),
    });
    const clamped = clampYm(def, companyMinMonth, companyMaxMonth);
    if (clamped) applyPayMonth(clamped);
  }, [
    finalizedMonthsReady,
    companyMinMonth,
    companyMaxMonth,
    finalizedMonths,
    applyPayMonth,
  ]);

  useEffect(() => {
    if (!finalizedMonthsReady || !payMonth || !companyMaxMonth) return;
    const clamped = clampYm(payMonth, companyMinMonth, companyMaxMonth);
    if (clamped && clamped !== payMonth) {
      applyPayMonth(payMonth, { toastIfClamped: true });
    }
  }, [
    finalizedMonthsReady,
    payMonth,
    companyMinMonth,
    companyMaxMonth,
    applyPayMonth,
  ]);

  const listedEmployeeIds = useMemo(
    () => listedEmployees.map((e) => e.id).filter(Boolean),
    [listedEmployees]
  );
  const listedEmployeeCount = listedEmployeeIds.length;
  const masterLists = scopeContext?.masterLists || {};

  /** Already processed in this run but not eligible for this month (e.g. LWD before month) */
  const staleExitProcessed = useMemo(() => {
    const eligibleIds = new Set(listedEmployees.map((e) => Number(e.id)));
    const rawById = new Map(
      (listedEmployeesRaw || []).map((e) => [Number(e.id), e])
    );
    return (processedRows || [])
      .filter((r) => r.status === "processed" && r.breakdownJson)
      .filter((r) => !eligibleIds.has(Number(r.employeeId)))
      .map((r) => {
        const emp = rawById.get(Number(r.employeeId));
        const b = r.breakdownJson || {};
        return {
          employeeId: Number(r.employeeId),
          empCode: emp?.empCode || b.empCode || String(r.employeeId),
          name: emp?.name || b.name || b.employeeName || "",
          exitYmd: String(
            emp?.relievedDate || emp?.terminationDate || ""
          ).slice(0, 10),
        };
      })
      .filter((s) => {
        const emp = rawById.get(s.employeeId);
        if (!emp) return true;
        return isEmployeeOutOfPayMonth(emp, payMonth);
      });
  }, [processedRows, listedEmployees, listedEmployeesRaw, payMonth]);

  /** Only trust run rows that belong to the selected pay month */
  const activeRun = run && String(run.payMonth || "").slice(0, 7) === String(payMonth).slice(0, 7) ? run : null;

  const steps = useMemo(() => {
    const raw = Array.isArray(activeRun?.stepsJson) ? activeRun.stepsJson : [];
    // TDS is computed inside Process Payroll — drop retired audit step from older runs
    const cleaned = raw.filter((s) => s?.key !== "tax_declarations");
    if (isAdmin) return cleaned;
    return cleaned.filter((s) =>
      canViewProcessStep(hasScreenAccess, "payroll", s.key, PAYROLL_STEP_RBAC)
    );
  }, [activeRun, isAdmin, hasScreenAccess]);
  const canMutateProcess = useMemo(
    () =>
      isAdmin || canEditProcessStep(hasScreenAccess, "payroll", "process", PAYROLL_STEP_RBAC),
    [isAdmin, hasScreenAccess]
  );
  const canEditActiveStep = useMemo(() => {
    if (isAdmin) return true;
    if (!activeStepKey) return false;
    return canEditProcessStep(hasScreenAccess, "payroll", activeStepKey, PAYROLL_STEP_RBAC);
  }, [isAdmin, hasScreenAccess, activeStepKey]);
  const canEditMonthlyVariables = useMemo(
    () =>
      isAdmin ||
      canEditProcessStep(hasScreenAccess, "payroll", "one_time_inputs", PAYROLL_STEP_RBAC),
    [isAdmin, hasScreenAccess]
  );
  const canImportMonthlyVariables = useMemo(
    () =>
      isAdmin ||
      hasScreenAccess("payroll", "pay_proc_one_time_inputs", "import") ||
      canEditMonthlyVariables,
    [isAdmin, hasScreenAccess, canEditMonthlyVariables]
  );
  const canEditChecklist = useMemo(
    () =>
      isAdmin ||
      canEditProcessStep(hasScreenAccess, "payroll", "checklist", PAYROLL_STEP_RBAC),
    [isAdmin, hasScreenAccess]
  );
  const canEditDisbursement = useMemo(
    () =>
      isAdmin ||
      canEditProcessStep(hasScreenAccess, "payroll", "disbursement", PAYROLL_STEP_RBAC),
    [isAdmin, hasScreenAccess]
  );
  const canApproveFinalise = useMemo(
    () =>
      isAdmin ||
      canApprove ||
      canApproveProcessStep(hasScreenAccess, "payroll", "finalise", PAYROLL_STEP_RBAC),
    [isAdmin, canApprove, hasScreenAccess]
  );
  const activeStep = steps.find((s) => s.key === activeStepKey) || steps[0];

  useEffect(() => {
    if (activeStepKey !== "process") setProcessHeaderExtras(null);
  }, [activeStepKey]);

  useEffect(() => {
    if (activeStepKey === "tax_declarations") setActiveStepKey("process");
  }, [activeStepKey, setActiveStepKey]);

  /** New Joinees rows missing first structure map, enriched from listedEmployees for apply. */
  const missingStructureEmployees = useMemo(() => {
    if (activeStepKey !== "new_joinees") return [];
    const rows = Array.isArray(activeStep?.resultJson?.rows) ? activeStep.resultJson.rows : [];
    if (!rows.length) return [];
    const byId = new Map((listedEmployees || []).map((e) => [Number(e.id), e]));
    const out = [];
    for (const row of rows) {
      const pending = Array.isArray(row.pendingFields) ? row.pendingFields : [];
      const missingStruct =
        row.checklist?.salaryStructure === false ||
        pending.some((p) => /salary\s*structure/i.test(String(p)));
      if (!missingStruct) continue;
      const full = byId.get(Number(row.employeeId));
      const ctc = Number(full?.ctc ?? full?.ctcAmount ?? 0);
      if (!full?.id || !(ctc > 0)) continue;
      out.push({
        ...full,
        id: full.id,
        empCode: full.empCode || row.empCode,
        name:
          [full.user?.name || full.name || row.name, full.user?.lastName || full.lastName]
            .filter(Boolean)
            .join(" ") || row.name,
        ctc,
      });
    }
    return out;
  }, [activeStepKey, activeStep?.resultJson?.rows, listedEmployees]);

  const processedIdSet = useMemo(() => {
    const set = new Set();
    (processedRows || []).forEach((r) => {
      // Bug 133: any processed row locks TDS override / edits — do not require breakdownJson
      if (String(r.status || "") === "processed") {
        const id = Number(r.employeeId);
        if (Number.isFinite(id) && id > 0) set.add(id);
      }
    });
    return set;
  }, [processedRows]);

  const processedEmployees = useMemo(() => {
    const fromListed = listedEmployees.filter((e) =>
      processedIdSet.has(Number(e.id))
    );
    const listedIds = new Set(fromListed.map((e) => Number(e.id)));
    const extras = (staleExitProcessed || [])
      .filter((s) => !listedIds.has(Number(s.employeeId)))
      .map((s) => ({
        id: s.employeeId,
        empCode: s.empCode,
        name: s.name,
        relievedDate: s.exitYmd,
      }));
    return [...fromListed, ...extras];
  }, [listedEmployees, processedIdSet, staleExitProcessed]);

  const unprocessedEmployees = useMemo(
    () => listedEmployees.filter((e) => !processedIdSet.has(Number(e.id))),
    [listedEmployees, processedIdSet]
  );

  /** In-scope processed only (never count other months / out-of-scope rows) */
  const processedCount = processedEmployees.length;

  const stepsFullyLocked = processedCount > 0 && unprocessedEmployees.length === 0;
  const monthFinalised = !!activeRun?.finalizedAt;
  /** Processed or finalised: browse freely, no re-validate, only Process/Finalise stay actionable. */
  const wizardViewLocked = monthFinalised || stepsFullyLocked;
  const wizardActionableKeys = useMemo(() => new Set(["process", "finalise"]), []);

  /** Validation / one-time: full list, or unprocessed-only once any are processed */
  const resolveEditableEmployeeIds = useCallback(() => {
    if (!processedCount) return listedEmployeeIds;
    return unprocessedEmployees.map((e) => e.id);
  }, [processedCount, listedEmployeeIds, unprocessedEmployees]);

  const validationPayload = useCallback(
    (stepKey) => ({
      employeeIds:
        stepKey === "process" ? listedEmployeeIds : resolveEditableEmployeeIds(),
      scopeMode: "all",
      listedCount: listedEmployeeCount,
      ignoredLeaveRequestIds,
    }),
    [listedEmployeeIds, resolveEditableEmployeeIds, listedEmployeeCount, ignoredLeaveRequestIds]
  );

  const notifyRelievedCleanup = useCallback((cleanup) => {
    const n = Number(cleanup?.cleanedCount || cleanup?.totalCleaned || 0);
    if (!n) return;
    const runs = Array.isArray(cleanup?.runs) ? cleanup.runs : [];
    const key = runs.length
      ? runs.map((r) => `${r.payMonth}:${r.cleanedCount}`).join("|")
      : `n:${n}`;
    if (relievedCleanupToastKeyRef.current === key) return;
    relievedCleanupToastKeyRef.current = key;
    const codes = runs
      .flatMap((r) => (r.employees || []).map((e) => e.empCode))
      .filter(Boolean)
      .slice(0, 6);
    const monthBits = runs
      .filter((r) => r.cleanedCount)
      .map((r) => `${r.payMonth} (${r.cleanedCount})`)
      .slice(0, 4);
    toast.success(
      `Removed ${n} relieved employee(s) from payroll` +
        (monthBits.length ? `: ${monthBits.join(", ")}` : "") +
        (codes.length ? ` — ${codes.join(", ")}` : "") +
        (n > codes.length && codes.length ? "…" : "") +
        ". Only master exit before the month; 0 paid-days employees kept.",
      { duration: 9000 }
    );
  }, []);

  const loadProcessedRows = useCallback(
    async (runId, month) => {
      if (!runId || !month) {
        setProcessedRows([]);
        return [];
      }
      try {
        const res = await payrollOpsService.getRunEmployees(runId);
        if (payMonthRef.current !== month) return [];
        if (res?.payMonth && String(res.payMonth).slice(0, 7) !== String(month).slice(0, 7)) {
          setProcessedRows([]);
          return [];
        }
        if (res?.relievedCleanup?.cleanedCount) {
          notifyRelievedCleanup({
            cleanedCount: res.relievedCleanup.cleanedCount,
            runs: [
              {
                payMonth: res.payMonth,
                cleanedCount: res.relievedCleanup.cleanedCount,
                employees: res.relievedCleanup.employees || [],
              },
            ],
          });
        }
        const rows = Array.isArray(res?.data) ? res.data : [];
        setProcessedRows(rows);
        return rows;
      } catch {
        if (payMonthRef.current === month) setProcessedRows([]);
        return [];
      }
    },
    [notifyRelievedCleanup]
  );

  useEffect(() => {
    if (!activeRun?.id) {
      setProcessedRows([]);
      return;
    }
    loadProcessedRows(activeRun.id, payMonth);
  }, [activeRun?.id, activeRun?.status, payMonth, loadProcessedRows]);

  useEffect(() => {
    const runId = Number(activeRun?.id) || 0;
    if (!runId || !steps.length) return;
    const month = String(payMonth || "").slice(0, 7);
    const payslipsPublished = !!activeRun?.payslipsPublishedAt;
    const monthFinalised = !!activeRun?.finalizedAt;
    const marker = `${runId}|${month}|${monthFinalised ? 1 : 0}|${payslipsPublished ? 1 : 0}`;
    if (autoLandingRunKeyRef.current === marker) return;
    autoLandingRunKeyRef.current = marker;
    const land = resolveProcessWizardLanding({
      steps,
      storageKey: `payroll-wizard-step:${runId}:${month}`,
      monthFinalised,
      payslipsPublished,
    });
    if (land) setActiveStepKey(land);
  }, [
    activeRun?.id,
    activeRun?.finalizedAt,
    activeRun?.payslipsPublishedAt,
    payMonth,
    steps,
    setActiveStepKey,
  ]);

  const taxStepCheckedFor = useRef(null); // reserved — Validate is manual only

  useEffect(() => {
    taxStepCheckedFor.current = null;
  }, [payMonth, activeRun?.id]);

  const loadRun = useCallback(async (month) => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setRun(null);
    setProcessedRows([]);
    try {
      const res = await payrollOpsService.getOrCreateRun(month);
      if (seq !== loadSeqRef.current || payMonthRef.current !== month) return;
      const next = res?.data || null;
      if (next && String(next.payMonth || "").slice(0, 7) !== String(month).slice(0, 7)) {
        toast.error("Pay month mismatch while loading run");
        setRun(null);
        return;
      }
      setRun(next);
      if (res?.relievedCleanup?.cleanedCount) {
        notifyRelievedCleanup(res.relievedCleanup);
      }
    } catch (err) {
      if (seq !== loadSeqRef.current || payMonthRef.current !== month) return;
      toast.error(err?.message || "Failed to load payroll run");
      setRun(null);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [notifyRelievedCleanup]);

  const handleRunUpdated = useCallback(
    (data) => {
      if (data) setRun(data);
      refreshFinalizedMonths();
    },
    [refreshFinalizedMonths]
  );

  /** Process/reprocess gated when immediate previous run exists and is not finalised. */
  const evaluatePreviousMonth = useCallback(async (month) => {
    const prev = getPreviousPayMonth(month);
    if (!prev || (companyMinMonth && prev < companyMinMonth)) {
      setPrevMonthBlocked(false);
      return;
    }
    try {
      const res = await payrollOpsService.findRun(prev);
      if (payMonthRef.current !== month) return;
      const prevRun = res?.data;
      setPrevMonthBlocked(!!prevRun && !prevRun.finalizedAt);
    } catch {
      if (payMonthRef.current === month) setPrevMonthBlocked(false);
    }
  }, [companyMinMonth]);

  /** Load/create a run only once the selectable range is known and payMonth sits inside it. */
  const payMonthInRange =
    finalizedMonthsReady && clampYm(payMonth, companyMinMonth, companyMaxMonth) === payMonth;

  useEffect(() => {
    if (!payMonthInRange) return;
    loadRun(payMonth);
  }, [payMonth, payMonthInRange, loadRun]);

  useEffect(() => {
    if (!payMonthInRange) return;
    evaluatePreviousMonth(payMonth);
  }, [payMonth, payMonthInRange, evaluatePreviousMonth]);

  useEffect(() => {
    const scoped = scopeContext?.payMonth;
    if (!scoped || !finalizedMonthsReady || applyingWizardMonthRef.current) return;
    const clamped = clampYm(scoped, companyMinMonth, companyMaxMonth);
    if (!clamped) return;
    if (clamped === payMonth) {
      if (clamped !== String(scoped).slice(0, 7)) onPayMonthChange?.(clamped);
      return;
    }
    applyPayMonth(scoped, { toastIfClamped: clamped !== String(scoped).slice(0, 7) });
  }, [
    scopeContext?.payMonth,
    payMonth,
    companyMinMonth,
    companyMaxMonth,
    finalizedMonthsReady,
    applyPayMonth,
    onPayMonthChange,
  ]);

  const requestPayMonthChange = (nextMonth) => {
    if (!nextMonth || nextMonth === payMonth || stepBusy) return;
    if (!finalizedMonthsReady) return;
    const clamped = clampYm(nextMonth, companyMinMonth, companyMaxMonth);
    if (clamped !== String(nextMonth).slice(0, 7)) {
      return;
    }
    applyPayMonth(clamped);
  };

  const handleValidateStep = async (
    stepKey,
    { acknowledge = false, showAllTds: showAllTdsOpt, refresh = false } = {}
  ) => {
    if (!activeRun?.id) return;
    const reviewWhenLocked = REVIEW_WHEN_LOCKED_KEYS.has(stepKey);
    // Refresh always reloads full-scope lists (including when all are processed).
    if (
      !refresh &&
      stepsFullyLocked &&
      !isPostProcessStep(stepKey) &&
      !reviewWhenLocked
    ) {
      toast.error("All employees are processed. Unprocess some before editing steps.");
      return;
    }
    const ids =
      refresh || isPostProcessStep(stepKey) || reviewWhenLocked
        ? listedEmployeeIds
        : resolveEditableEmployeeIds();
    if (!ids.length) {
      toast.error(
        processedCount && !refresh
          ? "No unprocessed employees in current scope"
          : "No employees in Assigned Structure for this month"
      );
      return;
    }
    const runId = activeRun.id;
    const month = payMonth;
    setStepBusy(true);
    try {
      const res = await payrollOpsService.validateStep(runId, {
        stepKey,
        acknowledge,
        showAllTds: showAllTdsOpt !== undefined ? showAllTdsOpt : showAllTds,
        ...(refresh
          ? {
              employeeIds: listedEmployeeIds,
              scopeMode: "all",
              listedCount: listedEmployeeCount,
              ignoredLeaveRequestIds,
            }
          : validationPayload(stepKey)),
      });
      if (payMonthRef.current !== month) return;
      setRun(res?.data || activeRun);
      const st = res?.step?.status || res?.result?.status;
      const sum = res?.result?.summary || {};
      if (refresh) {
        toast.success("List refreshed");
        return;
      }
      if (st === "failed") {
        if (stepKey === "new_joinees" && sum.incompleteNewJoinees > 0) {
          toast.error(`${sum.incompleteNewJoinees} new joinee(s) have incomplete mandatory fields`);
        } else if (stepKey === "attendance_validation" && sum.pendingApprovals > 0) {
          toast.error(`${sum.pendingApprovals} pending leave approval(s)`);
        } else if (stepKey === "preview_validate") {
          const labels = (sum.blockingStepLabels || []).filter((label) => {
            // Defensive: ignore Process Payroll when scope is only partially processed
            const isProcess = String(label).toLowerCase().includes("process");
            if (isProcess && processedCount > 0 && unprocessedEmployees.length > 0) {
              return false;
            }
            return true;
          });
          if (labels.length) {
            toast.error(
              `Still blocking: ${labels.join(", ")}. Open each step or use Ignore & proceed, then Validate again.`
            );
          } else {
            toast.success("Check complete");
          }
        } else {
          toast.error(`${res?.step?.label || stepKey}: check failed`);
        }
      } else if (st === "acknowledged" && stepKey === "attendance_validation" && !acknowledge) {
        toast(messageAttendanceNotFinalised(payMonth), { icon: "⚠️", duration: 7000 });
      } else {
        toast.success("Check complete");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Validation failed");
    } finally {
      setStepBusy(false);
    }
  };

  const handleRefreshActiveStep = async () => {
    if (!activeStep?.key || !activeRun?.id || stepBusy) return;
    const key = activeStep.key === "company_checklist" ? "checklist" : activeStep.key;

    if (key === "process") {
      setStepBusy(true);
      try {
        await loadRun(payMonth);
        await loadProcessedRows(activeRun.id, payMonth);
        toast.success("List refreshed");
      } catch (err) {
        toast.error(err?.message || "Failed to refresh");
      } finally {
        setStepBusy(false);
      }
      return;
    }

    if (key === "disbursement") {
      setStepBusy(true);
      try {
        await loadDisbursementRows(payMonth);
        setDisbursementTick((t) => t + 1);
        toast.success("List refreshed");
      } catch (err) {
        toast.error(err?.message || "Failed to refresh");
      } finally {
        setStepBusy(false);
      }
      return;
    }

    if (key === "finalise") {
      setStepBusy(true);
      try {
        await loadRun(payMonth);
        toast.success("List refreshed");
      } catch (err) {
        toast.error(err?.message || "Failed to refresh");
      } finally {
        setStepBusy(false);
      }
      return;
    }

    if (key === "payslips") {
      setStepBusy(true);
      try {
        await loadRun(payMonth);
        await loadProcessedRows(activeRun.id, payMonth);
        setPayslipReloadTick((t) => t + 1);
        toast.success("List refreshed");
      } catch (err) {
        toast.error(err?.message || "Failed to refresh");
      } finally {
        setStepBusy(false);
      }
      return;
    }

    await handleValidateStep(key, {
      refresh: true,
      showAllTds: undefined,
    });
  };

  const handleIgnoreStep = async (stepKey) => {
    if (!activeRun?.id || !stepKey) return;
    if (stepsFullyLocked && !isPostProcessStep(stepKey) && !REVIEW_WHEN_LOCKED_KEYS.has(stepKey)) {
      toast.error("All employees are processed. Unprocess some before editing steps.");
      return;
    }
    const runId = activeRun.id;
    const month = payMonth;
    setStepBusy(true);
    try {
      const res = await payrollOpsService.validateStep(runId, {
        stepKey,
        ignore: true,
        ...validationPayload(stepKey),
      });
      if (payMonthRef.current !== month) return;
      setRun(res?.data || activeRun);
      toast.success("Ignored — you can proceed after re-running Preview & Validate");
      if (activeStepKey === "preview_validate" && stepKey !== "preview_validate") {
        setTimeout(() => handleValidateStep("preview_validate"), 200);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Could not ignore step";
      toast.error(msg);
      if (
        stepKey === "new_joinees" &&
        /salary structure/i.test(String(msg)) &&
        (err?.response?.data?.missingStructureCount > 0 || /first map/i.test(String(msg)))
      ) {
        setApplyStructuresOpen(true);
      }
    } finally {
      setStepBusy(false);
    }
  };

  const openActionChooser = async (action, preferredStream = "both") => {
    if (
      ["process", "reprocess", "unprocess"].includes(action) &&
      !canMutateProcess
    ) {
      toast.error("No permission to Process / Reprocess / Unprocess. Grant Process Payroll · Edit.");
      return;
    }
    if (prevMonthBlocked && ["process", "reprocess", "unprocess"].includes(action)) {
      toast.error("Previous month payroll is not finalised. Finalise previous month first.");
      return;
    }
    if (action === "process" && !unprocessedEmployees.length) {
      toast.error("No unprocessed employees");
      return;
    }
    if ((action === "unprocess" || action === "reprocess") && !processedEmployees.length) {
      toast.error("No processed employees");
      return;
    }
    if (String(action).startsWith("disburse_")) {
      if (!processedCount) {
        toast.error("Process at least one employee before disbursement");
        return;
      }
      setDisbursementLoadingAction(action);
      try {
        const res = await payrollOpsService.listDisbursements(payMonth);
        const rows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
        setDisbursementRows(rows);
        const byId = new Map((listedEmployees || []).map((e) => [Number(e.id), e]));
        const toEmp = (r) => {
          const emp = byId.get(Number(r.employeeId)) || {};
          const name =
            r.empName ||
            emp.name ||
            emp.employeeName ||
            [emp.firstName, emp.lastName].filter(Boolean).join(" ").trim() ||
            "";
          return {
            ...emp,
            id: Number(r.employeeId),
            empCode: r.empCode || emp.empCode || String(r.employeeId),
            name,
            netPay: r.netPay,
            disbursementStatus: r.status || "pending",
          };
        };
        const pool = rows
          .filter((r) =>
            action === "disburse_unpay"
              ? String(r.status || "pending") !== "pending"
              : action === "disburse_paid"
                ? String(r.status || "pending") !== "paid"
                : String(r.status || "pending") === "pending"
          )
          .map(toEmp)
          .filter((e) => e.id > 0)
          .filter((e, idx, arr) => arr.findIndex((x) => Number(x.id) === Number(e.id)) === idx);
        if (!pool.length) {
          toast.error(
            action === "disburse_unpay"
              ? "No paid / hold employees to unpay"
              : rows.length
                ? "No actionable employees for this disbursement action"
                : "Could not load disbursement employees for this month"
          );
          return;
        }
        if (action === "disburse_partial") {
          setDisbursePartialForm({ holdMode: "amount", holdAmount: "", notes: "" });
        }
        if (action === "disburse_paid" || action === "disburse_partial") {
          setDisbursementPaymentDate((prev) => prev || new Date().toISOString().slice(0, 10));
        }
        const streamPref =
          preferredStream === "salary" || preferredStream === "fnf" ? preferredStream : "both";
        setDisburseStreamMode(streamPref);
        setActionChooser({ action, employees: pool, disbursementRows: rows, preferredStream: streamPref });
      } catch (err) {
        toast.error(
          err?.response?.data?.message || err?.message || "Failed to load disbursement employees"
        );
      } finally {
        setDisbursementLoadingAction(null);
      }
      return;
    }
    setActionChooser({ action });
  };

  const loadDisbursementRows = useCallback(async (month) => {
    if (!month) {
      setDisbursementRows([]);
      return [];
    }
    try {
      const res = await payrollOpsService.listDisbursements(month);
      if (payMonthRef.current !== month) return [];
      const rows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
      setDisbursementRows(rows);
      return rows;
    } catch {
      if (payMonthRef.current === month) setDisbursementRows([]);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!payMonth) return undefined;
    const needsDisb =
      activeStepKey === "disbursement"
        ? processedCount > 0
        : activeStepKey === "preview_validate"
          ? processedCount > 0
          : false;
    if (!needsDisb) return undefined;
    loadDisbursementRows(payMonth);
    return undefined;
  }, [activeStepKey, payMonth, processedCount, loadDisbursementRows, disbursementTick]);

  const unpaidDisbursementEmployees = useMemo(() => {
    const byId = new Map((listedEmployees || []).map((e) => [Number(e.id), e]));
    const seen = new Set();
    return (disbursementRows || [])
      .filter((r) => String(r.status || "pending") === "pending")
      .map((r) => {
        const eid = Number(r.employeeId);
        if (seen.has(eid)) return null;
        seen.add(eid);
        const emp = byId.get(eid) || {};
        const name =
          r.empName ||
          emp.name ||
          emp.employeeName ||
          [emp.firstName, emp.lastName].filter(Boolean).join(" ").trim() ||
          "";
        return {
          ...emp,
          id: eid,
          empCode: r.empCode || emp.empCode || String(r.employeeId),
          name,
          netPay: r.netPay,
        };
      })
      .filter((e) => e && e.id > 0);
  }, [disbursementRows, listedEmployees]);

  const settledDisbursementEmployees = useMemo(() => {
    const byId = new Map((listedEmployees || []).map((e) => [Number(e.id), e]));
    const seen = new Set();
    const unpayable = new Set(["paid", "hold", "partial_hold", "partial_paid"]);
    return (disbursementRows || [])
      .filter((r) => unpayable.has(String(r.status || "pending")))
      .map((r) => {
        const eid = Number(r.employeeId);
        if (seen.has(eid)) return null;
        seen.add(eid);
        const emp = byId.get(eid) || {};
        const name =
          r.empName ||
          emp.name ||
          emp.employeeName ||
          [emp.firstName, emp.lastName].filter(Boolean).join(" ").trim() ||
          "";
        return {
          ...emp,
          id: eid,
          empCode: r.empCode || emp.empCode || String(r.employeeId),
          name,
          netPay: r.netPay,
          disbursementStatus: r.status,
        };
      })
      .filter((e) => e && e.id > 0);
  }, [disbursementRows, listedEmployees]);

  const payEligibleDisbursementEmployees = useMemo(() => {
    const byId = new Map((listedEmployees || []).map((e) => [Number(e.id), e]));
    const seen = new Set();
    return (disbursementRows || [])
      .filter((r) => String(r.status || "pending") !== "paid")
      .map((r) => {
        const eid = Number(r.employeeId);
        if (seen.has(eid)) return null;
        seen.add(eid);
        const emp = byId.get(eid) || {};
        const name =
          r.empName ||
          emp.name ||
          emp.employeeName ||
          [emp.firstName, emp.lastName].filter(Boolean).join(" ").trim() ||
          "";
        return {
          ...emp,
          id: eid,
          empCode: r.empCode || emp.empCode || String(r.employeeId),
          name,
          netPay: r.netPay,
        };
      })
      .filter((e) => e && e.id > 0);
  }, [disbursementRows, listedEmployees]);

  const allDisbursementPaid = useMemo(() => {
    if (!disbursementRows.length) return false;
    return disbursementRows.every((r) => String(r.status) === "paid");
  }, [disbursementRows]);

  /** Pay + Hold + Partial together must cover every register row before Next. */
  const disbursementComplete = useMemo(() => {
    if (!disbursementRows.length) return false;
    return disbursementRows.every((r) =>
      ["paid", "hold", "partial_hold"].includes(String(r.status || "pending"))
    );
  }, [disbursementRows]);

  const handleDisbursementFromChooser = async (actionKey, employeeIds, streamMode = "both") => {
    const action =
      actionKey === "disburse_hold"
        ? "hold"
        : actionKey === "disburse_partial"
          ? "partial"
          : actionKey === "disburse_unpay"
            ? "unpay"
            : "paid";
    const ids = (Array.isArray(employeeIds) ? employeeIds : []).map(Number).filter((id) => id > 0);
    if (!ids.length || !payMonth) return;

    if (action === "partial") {
      const raw = Number(disbursePartialForm.holdAmount);
      if (!Number.isFinite(raw) || raw < 0) {
        toast.error(
          disbursePartialForm.holdMode === "percent"
            ? "Enter a valid percent"
            : "Enter a valid hold amount"
        );
        return;
      }
      if (disbursePartialForm.holdMode === "percent" && streamMode === "both") {
        toast.error("For percent-based partial, choose Salary only or F&F only");
        return;
      }
    }

    const rowsSnapshot =
      (Array.isArray(actionChooser?.disbursementRows) && actionChooser.disbursementRows.length
        ? actionChooser.disbursementRows
        : disbursementRows) || [];

    setActionChooser(null);
    setStepBusy(true);
    try {
      const selectedRows = rowsSnapshot.filter((r) => ids.includes(Number(r.employeeId)));
      if (action === "partial" && disbursePartialForm.holdMode === "percent") {
        let lastRes = null;
        for (const row of selectedRows) {
          if (
            streamMode !== "both" &&
            String(row.stream || "salary") !== String(streamMode)
          ) {
            continue;
          }
          const net = Number(row.netPay) || 0;
          const amt = Math.min(net, (net * Number(disbursePartialForm.holdAmount)) / 100);
          lastRes = await payrollOpsService.applyDisbursement({
            payMonth,
            employeeIds: [Number(row.employeeId)],
            action: "partial",
            stream: streamMode === "both" ? String(row.stream || "salary") : streamMode,
            holdAmount: amt,
            notes: disbursePartialForm.notes?.trim() || null,
            paymentDate: disbursementPaymentDate,
          });
        }
        if (lastRes?.run) setRun(lastRes.run);
      } else {
        const payload = {
          payMonth,
          employeeIds: ids,
          action,
          holdAmount: action === "partial" ? Number(disbursePartialForm.holdAmount) : null,
          notes: disbursePartialForm.notes?.trim() || null,
          paymentDate: disbursementPaymentDate,
        };
        if (streamMode === "salary" || streamMode === "fnf") {
          payload.stream = streamMode;
        }
        const res = await payrollOpsService.applyDisbursement({
          ...payload,
        });
        if (res?.run) setRun(res.run);
      }
      toast.success(
        action === "paid"
          ? `Paid ${ids.length} employee(s)`
          : action === "hold"
            ? `Held ${ids.length} employee(s)`
            : action === "unpay"
              ? `Unpaid ${ids.length} employee(s) — ready to pay again`
              : `Partial pay applied to ${ids.length}`
      );
      setDisbursementTick((t) => t + 1);
      await loadDisbursementRows(payMonth);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Disbursement failed");
    } finally {
      setStepBusy(false);
    }
  };

  const handleProcess = async (action, employeeIds) => {
    if (!activeRun?.id) return;
    if (prevMonthBlocked) {
      toast.error("Previous month payroll is not finalised. Finalise previous month first.");
      return;
    }

    const runId = activeRun.id;
    const month = payMonth;

    let targetIds = (Array.isArray(employeeIds) ? employeeIds : []).map(Number).filter((id) => id > 0);

    if (action === "process") {
      targetIds = targetIds.filter((id) => !processedIdSet.has(id));
    } else if (action === "reprocess" || action === "unprocess") {
      targetIds = targetIds.filter((id) => processedIdSet.has(id));
    }

    if (action === "process" || action === "reprocess") {
      const rawById = new Map(
        (listedEmployeesRaw || []).map((e) => [Number(e.id), e])
      );
      const blocked = targetIds.filter((id) =>
        isEmployeeOutOfPayMonth(rawById.get(id), month)
      );
      if (blocked.length) {
        toast.error(
          `${blocked.length} employee(s) left before ${formatPayMonthLabel(month)}. ` +
            `Unprocess / unpublish / unpay them in this month if needed — do not process them here.`,
          { duration: 10000 }
        );
        return;
      }
    }

    if (!targetIds.length) {
      toast.error(
        action === "process"
          ? "No unprocessed employees to process"
          : action === "unprocess"
            ? "No processed employees to unprocess"
            : "No processed employees to reprocess"
      );
      return;
    }

    setActionChooser(null);
    setStepBusy(true);
    const isPayCalc = action === "process" || action === "reprocess";
    setBatchProgress({
      action,
      phase: isPayCalc ? "payroll" : action,
      done: 0,
      total: targetIds.length,
    });

    const chunks = chunkIds(
      targetIds,
      action === "unprocess" ? UNPROCESS_CHUNK_SIZE : PROCESS_CHUNK_SIZE
    );
    let done = 0;
    let chunkIndex = 0;
    let lastRun = activeRun;
    let failed = false;
    let okCount = 0;
    let errorCount = 0;
    let payableMismatchCount = 0;
    let currentPhase = isPayCalc ? "payroll" : action;
    const errorSamples = [];
    const mismatchSamples = [];

    const runChunkBatch = async (chunkList, { phaseAction, tdsPhase, progressPhase }) => {
      let localDone = 0;
      let localOk = 0;
      let localErr = 0;
      currentPhase = progressPhase;
      setBatchProgress({
        action,
        phase: progressPhase,
        done: 0,
        total: targetIds.length,
      });
      for (const [i, chunk] of chunkList.entries()) {
        chunkIndex = i;
        if (payMonthRef.current !== month) break;
        const res = await payrollOpsService.processRun(runId, {
          action: phaseAction,
          confirmScope: true,
          acknowledgeWarnings: true,
          payMonth: month,
          employeeIds: chunk,
          scopeEmployeeIds: listedEmployeeIds,
          scopeMode: "all",
          listedCount: listedEmployeeCount,
          ...(tdsPhase ? { tdsPhase } : {}),
        });
        lastRun = res?.data || lastRun;
        localDone += chunk.length;
        done = localDone;
        localOk += Number(res?.employeeCount) || 0;
        localErr += Number(res?.errorCount) || 0;
        payableMismatchCount += Number(res?.payableMismatchCount) || 0;
        if (Array.isArray(res?.errors)) {
          res.errors.forEach((e) => {
            if (errorSamples.length < 5) errorSamples.push(e);
            if (
              e?.code === "ATTENDANCE_PAYABLE_MISMATCH" &&
              mismatchSamples.length < 3
            ) {
              mismatchSamples.push(e);
            }
          });
        }
        setBatchProgress({
          action,
          phase: progressPhase,
          done: localDone,
          total: targetIds.length,
        });
        if (payMonthRef.current === month) setRun(lastRun);
      }
      return { localOk, localErr };
    };

    try {
      if (action === "unprocess") {
        const u = await runChunkBatch(chunks, {
          phaseAction: "unprocess",
          progressPhase: "unprocess",
        });
        okCount = u.localOk;
        errorCount = u.localErr;
      } else {
        // Phase A — payroll calculation (earnings / statutory / variables)
        const pay = await runChunkBatch(chunks, {
          phaseAction: action,
          tdsPhase: "payroll",
          progressPhase: "payroll",
        });
        if (payMonthRef.current !== month) return;
        // Phase B — TDS from processed lines (same PayrollEmployeeTds rows)
        const tds = await runChunkBatch(chunks, {
          phaseAction: "reprocess",
          tdsPhase: "tds",
          progressPhase: "tds",
        });
        okCount = tds.localOk || pay.localOk;
        errorCount = Math.max(pay.localErr, tds.localErr);
      }
      if (payMonthRef.current !== month) return;
      if (action === "unprocess") {
        toast.success(`Unprocessed ${targetIds.length}`);
      } else if (payableMismatchCount > 0 || mismatchSamples.length > 0) {
        const sample = mismatchSamples[0] || errorSamples[0];
        const detail =
          sample?.message ||
          (sample?.empCode
            ? `${sample.empCode}: Process Attendance payable days do not match payroll`
            : "Process Attendance payable days do not match payroll");
        toast.error(
          `Blocked ${payableMismatchCount || mismatchSamples.length || errorCount} employee(s) — attendance payable mismatch. ${detail}`,
          { duration: 12000 }
        );
      } else if (errorCount > 0 && okCount === 0) {
        const sample = errorSamples[0];
        toast.error(
          `Process failed for ${errorCount} employee(s)` +
            (sample?.empCode || sample?.message
              ? `: ${sample.empCode ? `${sample.empCode} — ` : ""}${sample.message || ""}`
              : ". Check Failed tab — usually missing Salary Structure (first map).")
        );
      } else if (errorCount > 0) {
        toast(
          `${action === "reprocess" ? "Reprocessed" : "Processed"} ${okCount}; ${errorCount} failed (see Failed tab — often missing first salary map)`,
          { icon: "⚠️", duration: 6000 }
        );
      } else {
        toast.success(
          action === "reprocess"
            ? `Reprocessed ${okCount || targetIds.length} (payroll + TDS)`
            : `Processed ${okCount || targetIds.length} (payroll + TDS)`
        );
      }
      const rows = await loadProcessedRows(runId, month);
      if (payMonthRef.current === month && (action === "unprocess" || okCount > 0)) {
        setListTabSignal({
          tab: action === "unprocess" ? "unprocessed" : "processed",
          key: Date.now(),
        });
      }
      if (
        (action === "process" || action === "reprocess") &&
        payMonthRef.current === month &&
        countNegativeNetFromRows(rows) > 0
      ) {
        setRevealNegativeKey((k) => k + 1);
      }
      if (action !== "process" && action !== "reprocess") {
        setBreakdownRow(null);
      }
    } catch (err) {
      failed = true;
      const code = err?.response?.data?.code;
      const isNetworkStop = !err?.response && (err?.code === "ECONNABORTED" || /timeout|network/i.test(err?.message || ""));
      if ((isNetworkStop || code === "PAYROLL_PROCESS_IN_PROGRESS") && payMonthRef.current === month) {
        // Timed-out batch is still running on the server; a refused batch was never started.
        const resumeFrom = isNetworkStop ? chunkIndex + 1 : chunkIndex;
        setServerBusy({
          reason: isNetworkStop ? "timeout" : "other",
          action,
          phase: currentPhase,
          done,
          total: targetIds.length,
          lock: err?.response?.data?.lock || null,
          resumeIds: chunks.slice(resumeFrom).flat(),
        });
        await loadProcessedRows(runId, month);
        return;
      }
      const msg = err?.response?.data?.message || err?.message || "Action failed";
      toast.error(msg, {
        duration:
          code === "PAY_MONTH_EXIT_CONFLICT" || code === "ATTENDANCE_NOT_LOCKED"
            ? 12000
            : code === "PAYROLL_FINALISED" || code === "PAYROLL_PROCESSED"
              ? 10000
              : 4000,
        style:
          code === "PAY_MONTH_EXIT_CONFLICT" ||
          code === "ATTENDANCE_NOT_LOCKED" ||
          code === "PAYROLL_FINALISED" ||
          code === "PAYROLL_PROCESSED"
            ? { whiteSpace: "pre-line", maxWidth: 520 }
            : undefined,
      });
      if (payMonthRef.current === month) await loadProcessedRows(runId, month);
    } finally {
      setStepBusy(false);
      if (!failed) {
        setTimeout(() => setBatchProgress(null), 900);
      } else {
        setBatchProgress(null);
      }
    }
  };

  const activeRunId = activeRun?.id || null;

  useEffect(() => {
    setServerBusy(null);
    setPendingResume(null);
  }, [activeRunId]);

  // Opening Process Payroll while someone else's request is running → show the busy card.
  useEffect(() => {
    if (!activeRunId || activeStepKey !== "process" || stepBusy) return undefined;
    let cancelled = false;
    payrollOpsService
      .getProcessingLock(activeRunId)
      .then((lock) => {
        if (!cancelled && lock) {
          setServerBusy((prev) => prev || { reason: "other", lock, resumeIds: [] });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [activeRunId, activeStepKey]);

  // Poll the lock while the server is busy; when it frees, refresh and resume the rest.
  useEffect(() => {
    if (!serverBusy || !activeRunId) return undefined;
    const runId = activeRunId;
    const month = payMonth;
    let stopped = false;
    const tick = async () => {
      let lock;
      try {
        lock = await payrollOpsService.getProcessingLock(runId);
      } catch (e) {
        if (e?.response?.status !== 404) return;
        lock = null;
      }
      if (stopped || payMonthRef.current !== month) return;
      if (lock) {
        setServerBusy((prev) => (prev ? { ...prev, lock } : prev));
        return;
      }
      stopped = true;
      await loadProcessedRows(runId, month);
      const resumeIds = serverBusy.resumeIds || [];
      setServerBusy(null);
      if (resumeIds.length && serverBusy.action) {
        toast.success(`Server finished — continuing with the remaining ${resumeIds.length}`);
        setPendingResume({ action: serverBusy.action, ids: resumeIds });
      } else {
        toast.success("Server finished — lists updated");
      }
    };
    tick();
    const timer = setInterval(tick, 5000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverBusy?.reason, serverBusy?.action, activeRunId]);

  useEffect(() => {
    if (!pendingResume || stepBusy || serverBusy) return;
    const { action, ids } = pendingResume;
    setPendingResume(null);
    handleProcess(action, ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingResume, stepBusy, serverBusy]);

  const card = darkMode
    ? "rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-4"
    : "rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4 shadow-sm";

  const result = activeStep?.resultJson;
  const editableCount =
    activeStep?.key === "process" ? listedEmployeeCount : resolveEditableEmployeeIds().length;

  const activeStepIndex = steps.findIndex((s) => s.key === (activeStep?.key || activeStepKey));
  const prevStep = activeStepIndex > 0 ? steps[activeStepIndex - 1] : null;
  const nextStep =
    activeStepIndex >= 0 && activeStepIndex < steps.length - 1 ? steps[activeStepIndex + 1] : null;
  const stepsWithoutValidate = new Set(["process", "finalise", "disbursement", "payslips"]);

  const panelExtraActions = (() => {
    if (!activeStep) return null;
    if (activeStep.key === "new_joinees" && missingStructureEmployees.length > 0) {
      return (
        <DsButton
          type="button"
          size="sm"
          variant="outline"
          disabled={stepBusy}
          onClick={() => setApplyStructuresOpen(true)}
          title="Apply matched salary structures (or pick a fallback) for new joinees missing a first map"
        >
          Apply salary structures ({missingStructureEmployees.length})
        </DsButton>
      );
    }
    if (activeStep.key === "process") {
      return (
        <ProcessPayrollActions
          darkMode={darkMode}
          run={activeRun}
          steps={steps}
          unprocessedCount={unprocessedEmployees.length}
          processedCount={processedEmployees.length}
          stepBusy={stepBusy || !!serverBusy}
          serverBusy={!!serverBusy}
          onOpenAction={openActionChooser}
          prevMonthBlocked={prevMonthBlocked}
          finalized={!!activeRun?.finalizedAt}
          canMutate={canMutateProcess}
        />
      );
    }
    if (activeStep.key === "finalise") {
      return (
        <FinalisePayrollActions
          darkMode={darkMode}
          run={activeRun}
          stepsFullyLocked={stepsFullyLocked}
          canApprove={canApproveFinalise}
          onUpdated={handleRunUpdated}
        />
      );
    }
    if (activeStep.key === "disbursement") {
      return (
        <DisbursementActions
          darkMode={darkMode}
          hasProcessed={processedCount > 0}
          finalized={!!activeRun?.finalizedAt}
          unpaidCount={unpaidDisbursementEmployees.length}
          payEligibleCount={payEligibleDisbursementEmployees.length}
          settledCount={settledDisbursementEmployees.length}
          totalCount={disbursementRows.length}
          allPaid={allDisbursementPaid}
          complete={disbursementComplete}
          busy={stepBusy || !canEditDisbursement}
          loadingAction={disbursementLoadingAction}
          onOpenAction={canEditDisbursement ? openActionChooser : null}
        />
      );
    }
    return null;
  })();

  const stepActions = activeStep ? (
    <PayrollStepActions
      step={activeStep}
      stepBusy={stepBusy}
      employeeCount={
        activeStep.key === "checklist" ||
        activeStep.key === "company_checklist" ||
        activeStep.key === "preview_validate"
          ? listedEmployeeCount
          : editableCount
      }
      disabled={
        monthFinalised
          ? !wizardActionableKeys.has(activeStep.key)
          : activeStep.key === "preview_validate"
            ? !canEditActiveStep
            : activeStep.key === "checklist" || activeStep.key === "company_checklist"
              ? !stepsFullyLocked || !canEditChecklist
              : (stepsFullyLocked && !isPostProcessStep(activeStep.key)) || !canEditActiveStep
      }
      showValidate={
        !stepsWithoutValidate.has(activeStep.key) &&
        !monthFinalised &&
        canEditActiveStep &&
        !(stepsFullyLocked && !isPostProcessStep(activeStep.key) && !REVIEW_WHEN_LOCKED_KEYS.has(activeStep.key))
      }
      canGoPrevious={!!prevStep}
      canGoNext={!!nextStep}
      nextBlocked={false}
      nextBlockedTitle=""
      onPrevious={() => {
        if (!prevStep) return;
        // Only invalidate when the month is still editable — not after process/finalise.
        if (!wizardViewLocked) clearStepValidation(activeStep?.key);
        setActiveStepKey(prevStep.key);
      }}
      onNext={() => {
        nextStep && setActiveStepKey(nextStep.key);
      }}
      onRunCheck={() =>
        handleValidateStep(
          activeStep.key === "company_checklist" ? "checklist" : activeStep.key
        )
      }
      onIgnore={() =>
        handleIgnoreStep(activeStep.key === "company_checklist" ? "checklist" : activeStep.key)
      }
      onAcknowledge={() => handleValidateStep(activeStep.key, { acknowledge: true })}
      extraActions={panelExtraActions}
      darkMode={darkMode}
    />
  ) : null;

  const renderStepPanel = () => {
    if (!activeStep) return null;

    if (activeStep.key === "one_time_inputs") {
      return (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <div className="min-h-0 flex-1 overflow-hidden">
            <PayrollOneTimeInputsPanel
              darkMode={darkMode}
              payMonth={payMonth}
              employees={unprocessedEmployees}
              employeeLookup={scopeContext?.listedEmployees || unprocessedEmployees}
              readOnly={stepsFullyLocked || !canEditMonthlyVariables}
              canImport={!stepsFullyLocked && canImportMonthlyVariables}
            />
            {result && (
              <div className="mt-3 min-h-0 max-h-[42vh] overflow-auto">
                <RunPayrollStepViews
                  stepKey="one_time_inputs"
                  result={result}
                  payMonth={payMonth}
                  darkMode={darkMode}
                />
              </div>
            )}
          </div>
        </div>
      );
    }

    if (activeStep.key === "checklist" || activeStep.key === "company_checklist") {
      return (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          {!stepsFullyLocked ? (
            <div
              className={`rounded-lg border px-3 py-2 text-xs ${
                darkMode
                  ? "border-amber-700/50 bg-amber-950/40 text-amber-100"
                  : "border-amber-200 bg-amber-50 text-amber-900"
              }`}
            >
              Process all employees first before completing the Checklist.
            </div>
          ) : null}
          <div className="min-h-0 flex-1 overflow-auto">
            <PayrollChecklistPanel
              darkMode={darkMode}
              payMonth={payMonth}
              readOnly={!stepsFullyLocked || !canEditChecklist}
            />
            {result && (
              <div className="mt-3">
                <RunPayrollStepViews
                  stepKey="checklist"
                  result={result}
                  payMonth={payMonth}
                  darkMode={darkMode}
                />
              </div>
            )}
          </div>
        </div>
      );
    }

    if (activeStep.key === "process") {
      return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          <ProcessPayrollPanel
            darkMode={darkMode}
            run={activeRun}
            payMonth={payMonth}
            steps={steps}
            unprocessedCount={unprocessedEmployees.length}
            processedCount={processedEmployees.length}
            stepBusy={stepBusy}
            batchProgress={batchProgress}
            onOpenAction={openActionChooser}
            processedRows={processedRows}
            unprocessedEmployees={unprocessedEmployees}
            onViewBreakdown={(row) => setBreakdownRow(row)}
            onGoToStep={setActiveStepKey}
            prevMonthBlocked={prevMonthBlocked}
            finalized={!!activeRun?.finalizedAt}
            revealNegativeKey={revealNegativeKey}
            listTabSignal={listTabSignal}
            serverBusy={serverBusy}
            processedIdSet={processedIdSet}
            onHeaderExtras={setProcessHeaderExtras}
            onReloadProcessed={() => {
              if (activeRun?.id) loadProcessedRows(activeRun.id, payMonth);
            }}
          />
        </div>
      );
    }

    if (activeStep.key === "finalise") {
      return (
        <PayrollFinalisePanel
          darkMode={darkMode}
          run={activeRun}
          stepsFullyLocked={stepsFullyLocked}
          canApprove={canApproveFinalise}
        />
      );
    }

    if (activeStep.key === "disbursement") {
      return (
        <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
          <PayrollDisbursementPanel
            darkMode={darkMode}
            payMonth={payMonth}
            hasProcessed={processedCount > 0}
            allProcessed={stepsFullyLocked}
            finalized={!!activeRun?.finalizedAt}
            streamMode="both"
            onRowsChange={setDisbursementRows}
            reloadToken={disbursementTick}
            paymentDate={disbursementPaymentDate}
          />
        </div>
      );
    }

    if (activeStep.key === "payslips") {
      return (
        <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
          <PayrollPayslipsPanel
            darkMode={darkMode}
            run={activeRun}
            payMonth={payMonth}
            finalized={!!activeRun?.finalizedAt}
            masterLists={masterLists}
            processedEmployees={processedEmployees}
            reloadToken={payslipReloadTick}
            onUpdated={handleRunUpdated}
          />
        </div>
      );
    }

    return (
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
        <div className="min-h-0 flex-1 overflow-auto overscroll-contain">
          <RunPayrollStepViews
            stepKey={activeStep.key}
            result={result}
            payMonth={payMonth}
            darkMode={darkMode}
            showAllTds={showAllTds}
            processedIdSet={processedIdSet}
            onToggleShowAllTds={(checked) => setShowAllTds(checked)}
            onRefreshStep={(key) =>
              handleValidateStep(key, { showAllTds, refresh: true })
            }
            onPendingLeaveDone={() => {}}
            onIgnoreLeaveIds={(ids) => {
              setIgnoredLeaveRequestIds((prev) => [...new Set([...prev, ...ids])]);
            }}
            onIgnoreStep={handleIgnoreStep}
            onGoToStep={setActiveStepKey}
            salaryStructures={salaryStructures}
            statutoryConfigJson={statutoryConfigJson}
            branchesList={branchesList}
            listedEmployees={scopeContext?.listedEmployees || []}
            wizardSteps={steps}
            previewExtras={{
              processedCount,
              listedCount: listedEmployeeCount,
              disbPaid: disbursementRows.filter((r) => String(r.status) === "paid").length,
              disbHold: disbursementRows.filter((r) =>
                ["hold", "partial_hold"].includes(String(r.status))
              ).length,
              disbPending: disbursementRows.filter(
                (r) => !["paid", "hold", "partial_hold"].includes(String(r.status || "pending"))
              ).length,
              disbTotal: disbursementRows.length,
              checklistCompleted: Number(
                steps.find((s) => s.key === "checklist" || s.key === "company_checklist")
                  ?.resultJson?.summary?.completed ?? 0
              ),
              checklistTotal: Number(
                steps.find((s) => s.key === "checklist" || s.key === "company_checklist")
                  ?.resultJson?.summary?.totalItems ?? 0
              ),
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-2 overflow-hidden">
      <div className="flex shrink-0 flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className={`text-lg font-semibold ${darkMode ? "text-white" : "text-[hsl(var(--ds-fg))]"}`}>
            Run Payroll · {formatPayMonthLabel(payMonth)}
          </h2>
          <p className={`text-xs ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
            {listedEmployeeCount} in scope
            {processedCount > 0
              ? ` · ${processedCount} processed · ${unprocessedEmployees.length} unprocessed`
              : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {activeRun?.finalizedAt ? (
            <span className="mb-2 inline-flex items-center rounded-full border border-emerald-500/40 bg-emerald-500 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white shadow-sm">
              Finalised
            </span>
          ) : null}
          <PayMonthNav
            id="run-payroll-pay-month"
            label="Pay month"
            value={payMonth}
            min={companyMinMonth || ""}
            max={companyMaxMonth || ""}
            disabled={stepBusy || !finalizedMonthsReady}
            darkMode={darkMode}
            onChange={requestPayMonthChange}
          />
        </div>
      </div>

      {staleExitProcessed.length > 0 && (
        <div
          className={`shrink-0 rounded-lg border px-3 py-2 text-xs ${
            darkMode
              ? "border-amber-700/50 bg-amber-950/40 text-amber-100"
              : "border-amber-200 bg-amber-50 text-amber-950"
          }`}
        >
          <p className="font-semibold">
            {staleExitProcessed.length} employee(s) in this pay month left earlier (master Relieving /
            Termination date).
          </p>
          <p className="mt-1">
            Clear them here before continuing: Unpublish → Unpay → Unprocess
            {staleExitProcessed
              .slice(0, 4)
              .map((s) => ` ${s.empCode}`)
              .join(",")}
            {staleExitProcessed.length > 4
              ? ` +${staleExitProcessed.length - 4} more`
              : ""}
            . Do not change master dates to force them into {formatPayMonthLabel(payMonth)}.
          </p>
        </div>
      )}

      {loading && !activeRun ? (
        <p className="text-sm text-[hsl(var(--ds-muted-fg))]">Loading…</p>
      ) : loading && activeRun ? (
        <div className={`${card} opacity-60`}>
          <p className="text-xs text-[hsl(var(--ds-muted-fg))]">Loading run…</p>
        </div>
      ) : !activeRun ? (
        <p className="text-sm text-amber-700">Could not load payroll run for this month. Try another month or refresh.</p>
      ) : (
        <ProcessWorkflowShell
          accent="payroll"
          wizard={
            <ProcessWizardStrip
              steps={steps.map((s) => ({ ...s, label: stepDisplayLabel(s) }))}
              activeKey={activeStepKey}
              visitedKeys={visitedKeys}
              onSelect={setActiveStepKey}
              accent="payroll"
              darkMode={darkMode}
              statusLabel={
                monthFinalised
                  ? "finalised"
                  : stepsFullyLocked
                    ? "processed"
                    : activeRun?.status || "draft"
              }
              viewLocked={wizardViewLocked}
              actionableKeys={wizardActionableKeys}
            />
          }
        >
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="mb-2 flex shrink-0 flex-nowrap items-center gap-2 overflow-x-auto overflow-y-visible">
              <h3 className="min-w-0 truncate text-sm font-semibold">
                {stepDisplayLabel(activeStep)}
              </h3>
              {activeStep?.key === "process" && processHeaderExtras?.left
                ? processHeaderExtras.left
                : null}
              <div className="relative z-50 ml-auto flex shrink-0 items-center gap-2 overflow-visible">
                {activeStep?.key === "process" && processHeaderExtras?.right
                  ? processHeaderExtras.right
                  : null}
                {activeStep && (
                  <button
                    type="button"
                    disabled={
                      stepBusy ||
                      (!stepsWithoutValidate.has(activeStep.key) &&
                        activeStep.key !== "process" &&
                        !listedEmployeeCount)
                    }
                    onClick={handleRefreshActiveStep}
                    title="Refresh — reload latest employees / missing records for this step"
                    aria-label="Refresh step list"
                    className={`relative z-0 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border disabled:opacity-40 ${
                      darkMode
                        ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))] hover:border-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))]"
                        : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:border-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))]"
                    }`}
                  >
                    {stepBusy ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <RefreshCw size={14} />
                    )}
                  </button>
                )}
              </div>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="min-h-0 flex-1 overflow-auto pr-0.5">{renderStepPanel()}</div>
              {activeStep && <div className="shrink-0">{stepActions}</div>}
            </div>
          </div>
        </ProcessWorkflowShell>
      )}

      {actionChooser && (
        <PayrollActionChooserModal
          darkMode={darkMode}
          open={!!actionChooser}
          title={
            actionChooser.action === "process"
              ? "Process payroll"
              : actionChooser.action === "unprocess"
                ? "Unprocess payroll"
                : actionChooser.action === "reprocess"
                  ? "Reprocess payroll"
                  : actionChooser.action === "disburse_paid"
                    ? "Pay"
                    : actionChooser.action === "disburse_hold"
                      ? "Hold"
                      : actionChooser.action === "disburse_partial"
                        ? "Pay partial"
                        : actionChooser.action === "disburse_unpay"
                          ? "Unpay"
                          : "Choose employees"
          }
          subtitle={
            actionChooser.action === "disburse_unpay"
              ? "Paid / hold employees — revert to unpaid (All / Selected / Group-wise). Choose Both / Salary / F&F below."
              : String(actionChooser.action).startsWith("disburse_")
                ? "Apply disbursement by employee (All / Selected / Group-wise). Choose Both / Salary / F&F below."
                : actionChooser.action === "process"
                  ? "Choose unprocessed employees (default: all)"
                  : "Choose processed employees (default: all)"
          }
          employees={
            Array.isArray(actionChooser.employees) && actionChooser.employees.length
              ? actionChooser.employees
              : actionChooser.action === "disburse_unpay"
                ? settledDisbursementEmployees
                : String(actionChooser.action).startsWith("disburse_")
                  ? actionChooser.action === "disburse_paid"
                    ? payEligibleDisbursementEmployees
                    : unpaidDisbursementEmployees
                  : actionChooser.action === "process"
                    ? unprocessedEmployees
                    : processedEmployees
          }
          masterLists={masterLists}
          confirmLabel={
            actionChooser.action === "process"
              ? "Process"
              : actionChooser.action === "unprocess"
                ? "Unprocess"
                : actionChooser.action === "reprocess"
                  ? "Reprocess"
                  : actionChooser.action === "disburse_paid"
                    ? "Pay"
                    : actionChooser.action === "disburse_hold"
                      ? "Hold"
                      : actionChooser.action === "disburse_partial"
                        ? "Apply partial"
                        : actionChooser.action === "disburse_unpay"
                          ? "Unpay"
                          : "Confirm"
          }
          confirmDisabled={
            ((actionChooser.action === "disburse_paid" ||
              actionChooser.action === "disburse_partial") &&
              !String(disbursementPaymentDate || "").trim()) ||
            (actionChooser.action === "disburse_partial" &&
              String(disbursePartialForm.holdAmount).trim() === "") ||
            (actionChooser.action === "disburse_partial" &&
              disbursePartialForm.holdMode === "percent" &&
              disburseStreamMode === "both")
          }
          extraContent={
            String(actionChooser.action).startsWith("disburse_") ? (
              <div className="space-y-2 rounded-lg border border-[hsl(var(--ds-border))] p-3">
                <div>
                  <p className="text-xs font-medium">Disbursement target stream</p>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {[
                      ["both", "Both (Salary + F&F)"],
                      ["salary", "Salary only"],
                      ["fnf", "F&F only"],
                    ].map(([id, label]) => (
                      <label key={id} className="inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px]">
                        <input
                          type="radio"
                          name="disburse-stream-mode"
                          checked={disburseStreamMode === id}
                          onChange={() => setDisburseStreamMode(id)}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
                {(actionChooser.action === "disburse_paid" ||
                  actionChooser.action === "disburse_partial") && (
                  <label className="block space-y-1">
                    <span className="text-xs font-medium">Payment date</span>
                    <input
                      type="date"
                      required
                      value={disbursementPaymentDate || ""}
                      onChange={(e) => setDisbursementPaymentDate(e.target.value)}
                      className={`w-full rounded-lg border px-3 py-1.5 text-xs outline-none focus:border-[hsl(var(--ds-primary))] ${
                        darkMode
                          ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                          : "border-[hsl(var(--ds-border))]"
                      }`}
                    />
                  </label>
                )}
                {actionChooser.action === "disburse_partial" ? (
                  <>
                    <p className="text-xs font-medium">Hold amount from net (remainder is paid)</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setDisbursePartialForm((p) => ({ ...p, holdMode: "amount" }))
                        }
                        className={`rounded-full px-2.5 py-1 text-[11px] ${
                          disbursePartialForm.holdMode === "amount"
                            ? "bg-[hsl(var(--ds-primary))] text-white"
                            : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
                        }`}
                      >
                        Amount
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setDisbursePartialForm((p) => ({ ...p, holdMode: "percent" }))
                        }
                        className={`rounded-full px-2.5 py-1 text-[11px] ${
                          disbursePartialForm.holdMode === "percent"
                            ? "bg-[hsl(var(--ds-primary))] text-white"
                            : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
                        }`}
                      >
                        Percent %
                      </button>
                    </div>
                    {disbursePartialForm.holdMode === "percent" && disburseStreamMode === "both" ? (
                      <p className="text-[11px] text-amber-700">
                        For percentage partial hold, choose Salary only or F&F only.
                      </p>
                    ) : null}
                    <input
                      type="number"
                      min="0"
                      step="1"
                      className={`w-full rounded-lg border px-3 py-1.5 text-xs outline-none focus:border-[hsl(var(--ds-primary))] ${
                        darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]" : "border-[hsl(var(--ds-border))]"
                      }`}
                      placeholder={
                        disbursePartialForm.holdMode === "percent" ? "e.g. 25" : "Hold amount"
                      }
                      value={disbursePartialForm.holdAmount}
                      onChange={(e) =>
                        setDisbursePartialForm((p) => ({ ...p, holdAmount: e.target.value }))
                      }
                    />
                  </>
                ) : null}
                <input
                  type="text"
                  className={`w-full rounded-lg border px-3 py-1.5 text-xs outline-none focus:border-[hsl(var(--ds-primary))] ${
                    darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]" : "border-[hsl(var(--ds-border))]"
                  }`}
                  placeholder="Notes (optional)"
                  value={disbursePartialForm.notes}
                  onChange={(e) =>
                    setDisbursePartialForm((p) => ({ ...p, notes: e.target.value }))
                  }
                />
              </div>
            ) : null
          }
          onClose={() => setActionChooser(null)}
          onConfirm={({ employeeIds }) => {
            if (String(actionChooser.action).startsWith("disburse_")) {
              handleDisbursementFromChooser(
                actionChooser.action,
                employeeIds,
                disburseStreamMode
              );
            } else {
              handleProcess(actionChooser.action, employeeIds);
            }
          }}
        />
      )}

      {breakdownRow && (
        <PayrollBreakdownModal
          darkMode={darkMode}
          row={breakdownRow}
          onClose={() => setBreakdownRow(null)}
          employee={(() => {
            const emp =
              listedEmployees.find((e) => Number(e.id) === Number(breakdownRow.employeeId)) || null;
            if (!emp) {
              return {
                id: breakdownRow.employeeId,
                ctc: breakdownRow.breakdownJson?.annualCtc || breakdownRow.breakdown?.annualCtc,
              };
            }
            return {
              ...emp,
              ctc:
                emp.ctc ||
                emp.ctcAmount ||
                breakdownRow.breakdownJson?.annualCtc ||
                breakdownRow.breakdown?.annualCtc,
            };
          })()}
          salaryStructures={salaryStructures}
          statutoryConfigJson={statutoryConfigJson}
          branchesList={branchesList}
          statutoryOverridesByEmployeeId={statutoryOverridesByEmployeeId}
        />
      )}

      {applyStructuresOpen ? (
        <ApplyAutoStructuresModal
          title="Apply salary structures — New Joinees"
          employees={missingStructureEmployees}
          onClose={async (didApply) => {
            setApplyStructuresOpen(false);
            if (!didApply) return;
            toast.success("Structures saved — re-validating New Joinees…");
            await handleValidateStep("new_joinees", { refresh: true });
          }}
        />
      ) : null}
    </div>
  );
}
